import { mergeAttributes } from '@tiptap/core';
import { type NodeType } from '@tiptap/pm/model';
import classNames from 'classnames';

import { Node_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import {
  type DOMOutputSpec,
  type ExtensionProps,
  type NodeConfig,
  type RicosExtension,
} from './../../ricos-types';
import { createRicosNodeExtension } from './../../plugin-commons';
import type { TiptapAdapterServices } from './../../tiptap-editor/initializeTiptapAdapter';

import {
  createIndentationStylingDecorationPlugin,
  createListItemSizingDecorationPlugin,
  createListStylePositionDecorationPlugin,
} from '../decoration-plugins';
import styles from '../statics/styles-scss';
import {
  createHandleTextWrapPlugin,
  createMergingSiblingListNodesTransactionPlugin,
  createOffsetNormalizingTransactionPlugin,
} from '../transaction-plugins';
import { indentList } from './commands/indentList';
import { outdentList } from './commands/outdentList';
import { toggleLists } from './commands/toggleLists';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    listItem: {
      /**
       * increase list indentation
       */
      indentList: () => ReturnType;
      /**
       * decrease list indentation
       */
      outdentList: () => ReturnType;
      /**
       * Toggle between different list types.
       */
      toggleLists: (
        listTypeOrName: string | NodeType,
        itemTypeOrName: string | NodeType
      ) => ReturnType;
    };
  }
}

export const listItem = createRicosNodeExtension({
  groups: ['shortcuts-enabled'],
  type: 'html-node',
  name: Node_Type.LIST_ITEM,
  reconfigure: (
    config: NodeConfig,
    _extensions: RicosExtension[],
    _props: ExtensionProps,
    settings: Record<string, unknown>,
    services: TiptapAdapterServices
  ) => {
    return {
      ...config,
      addOptions() {
        return {
          services,
          HTMLAttributes: {
            class: styles.listItem,
            dir: 'auto',
          },
          ...settings,
        };
      },
    };
  },
  createExtensionConfig() {
    return {
      name: this.name,
      priority: 101,

      content: '(PARAGRAPH|listItemFirstChildContent) listItemSecondChildContent*',

      defining: true,

      parseHTML() {
        return [
          {
            tag: 'li',
          },
        ];
      },

      renderHTML({ HTMLAttributes }) {
        const allAttributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);

        const attrs = {
          ...allAttributes,
          class: classNames(allAttributes.class),
          dir: 'auto',
        };

        return ['li', attrs, 0] as DOMOutputSpec;
      },

      addCommands() {
        return {
          toggleLists,
          indentList,
          outdentList,
        };
      },

      addKeyboardShortcuts() {
        const newLine = () => {
          return this.editor.commands.splitListItem(this.name);
        };

        const tiptapKeyboardShortcuts = {
          Enter: newLine,
        };

        //TODO: append extension specific code to existing new line command

        const newLineShortcuts: any = [];
        this.options.services.shortcuts.shortcuts.forEach((shortcut: $TSFixMe) => {
          if (shortcut.shortcut.name === 'NewLine') {
            newLineShortcuts.push(shortcut);
          }
        });

        newLineShortcuts.forEach((newLineShortcut: $TSFixMe) => {
          const modifier = newLineShortcut.keys.macOs.modifiers[0];
          const key = newLineShortcut.keys.macOs.key;
          const shortcutKey: $TSFixMeKey<typeof tiptapKeyboardShortcuts> = modifier
            ? `${modifier}-${key}`
            : key;
          tiptapKeyboardShortcuts[shortcutKey] = newLine;
        });

        return {
          ...tiptapKeyboardShortcuts,
        };
      },
      addProseMirrorPlugins() {
        return [
          createIndentationStylingDecorationPlugin(),
          createListItemSizingDecorationPlugin(),
          createListStylePositionDecorationPlugin(),

          createMergingSiblingListNodesTransactionPlugin(),
          createOffsetNormalizingTransactionPlugin(),
          createHandleTextWrapPlugin(),
        ];
      },
    };
  },
});
